import { UseFormReturn } from "@/lib/form/form"
import { FormSchema } from "@/lib/form/schema"
import {
  FormData,
  FormParamStatus,
  ParamData,
  ProductFormData,
  isSubmittedLikeStatus,
} from "@/lib/form/types"
import { toLineStatusKey } from "@/store/ipqc"
import { isEmpty, isNil, toPath } from "lodash"
import * as y from "yup"

export function schemaToFormData(
  schema: FormSchema,
  lineStatuses: Record<string, FormParamStatus | undefined>,
  sessionId: string
): FormData {
  const data: FormData = {
    products: [],
  }

  data.products = schema.products.map((product): ProductFormData => {
    return {
      id: product.id,
      productionLines: product.productionLines.map((line) => {
        return {
          id: line.id,
          machines: line.machines.map((machine) => {
            return {
              id: machine.id,
              breakdown: machine.breakdown || [],
              internalProcesses: machine.internalProcesses.map((proc) => {
                return {
                  id: proc.id,
                  parameters: proc.parameters.map((param): ParamData => {
                    if (param.window?.length) {
                      const window = param.window.map((curr) => ({
                        id: curr.id ?? "", // ensure string
                        startTime: curr.startTime,
                        endTime: curr.endTime,
                        value: curr.value,
                        remark: curr.remark,
                        resample: curr.resample,
                        resampleCleared: curr.resampleCleared,
                        recordedAt: curr.recordedAt,
                        status: getFieldStatus(
                          curr.status,
                          lineStatuses[
                            toLineStatusKey({
                              productId: product.id,
                              lineId: line.id,
                              sessionId: sessionId,
                            })
                          ]
                        ),
                      }))

                      return {
                        id: param.id ?? "",
                        formula: !!param.formula,
                        window: window,
                        value: param.value || "",
                      } as ParamData
                    }

                    return {
                      id: param.id ?? "",
                      remark: param.remark,
                      resample: param.resample,
                      resampleCleared: param.resampleCleared,
                      gradeLabel: param.gradeLabel,
                      recordedAt: param.recordedAt ?? "",
                      formula: !!param.formula,
                      value: param.value,
                      status: getFieldStatus(
                        param.status,
                        lineStatuses[
                          toLineStatusKey({
                            productId: product.id,
                            lineId: line.id,
                            sessionId: sessionId,
                          })
                        ]
                      ),
                    } as ParamData
                  }),
                }
              }),
            }
          }),
        }
      }),
    }
  })

  return data
}

function getFieldStatus(
  status: FormParamStatus | undefined,
  lineStatus: FormParamStatus | undefined
): FormParamStatus {
  // If the line is submitted, all parameters should show submitted UI
  if (isSubmittedLikeStatus(lineStatus)) {
    // Preserve special statuses that have their own rendering
    if (
      status === FormParamStatus.NOT_APPLICABLE ||
      status === FormParamStatus.DISABLED ||
      status === FormParamStatus.BREAK_DOWN ||
      status === FormParamStatus.CHANGE_OVER ||
      status === FormParamStatus.POWER_FAILURE ||
      status === FormParamStatus.OTHER
    ) {
      return status
    }
    return FormParamStatus.SUBMITTED
  }

  if (!status) {
    return FormParamStatus.DRAFT
  }

  // Pending reading states should remain editable in the form UI.
  if (status === FormParamStatus.PENDING || status === FormParamStatus.DRAFT) {
    return FormParamStatus.DRAFT
  }

  if (
    isSubmittedLikeStatus(status) ||
    status === FormParamStatus.FILLED_COMPLETE
  ) {
    return FormParamStatus.SUBMITTED
  }

  return status
}

function createFilledValidator(): y.Schema {
  const fieldValidator = y.object({
    value: y.mixed().optional(),
  })

  const paramValidator = y
    .object({})
    .concat(fieldValidator)
    .concat(
      y.object({
        window: y.array(fieldValidator),
      })
    )
    .test({
      name: "test-value-filled",
      test(value, ctx) {
        if (value.window?.length) {
          for (const window of value.window) {
            if (isFilled(window.value)) {
              return ctx.createError({ message: ctx.path })
            }
          }
          return true
        }

        if (isFilled(value.value)) {
          return ctx.createError({ message: ctx.path })
        }

        return true
      },
    })

  const validator = y.object({
    products: y.array(
      y.object({
        productionLines: y.array(
          y.object({
            machines: y.array(
              y.object({
                internalProcesses: y.array(
                  y.object({
                    parameters: y.array(paramValidator),
                  })
                ),
              })
            ),
          })
        ),
      })
    ),
  })

  return validator
}

export function isFilled(val: any): boolean {
  if (
    typeof val === "number" ||
    typeof val === "bigint" ||
    typeof val === "boolean"
  ) {
    return true
  }

  return !(isEmpty(val) || isNil(val))
}

const filledValidator = createFilledValidator()

export interface FormFilledStatus {
  products: {
    filled: boolean
    lines: boolean[]
  }[]
}

export function getFormFilledStatus(form: FormData): Record<string, boolean> {
  const result: Record<string, boolean> = {}

  try {
    filledValidator.validateSync(form, { abortEarly: false })
  } catch (err) {
    if (err instanceof y.ValidationError) {
      const filledLines = new Set(
        err.errors.map((path) => {
          const parts = toPath(path)
          return `${parts[1]}-${parts[3]}`
        })
      )

      for (const line of filledLines) {
        result[line] = true
      }
    }
  }

  return result
}

export function traverseIPQCForm(
  form: UseFormReturn,
  cb: (
    path: string,
    value: ParamData,
    context: {
      prodIdx: number
      lineIdx: number
      stageIdx: number
      procIdx: number
      paramIdx: number
    }
  ) => void
) {
  form.state.value.values.products.forEach(
    (product: ProductFormData, productIndex: number) => {
      const productPath = `products[${productIndex}]`

      product.productionLines.forEach((line, lineIndex) => {
        const linePath = `${productPath}.productionLines[${lineIndex}]`

        line.machines.forEach((stage, stageIndex) => {
          const stagePath = `${linePath}.machines[${stageIndex}]`

          stage.internalProcesses.forEach((proc, procIndex) => {
            const procPath = `${stagePath}.internalProcesses[${procIndex}]`

            proc.parameters.forEach((param, paramIndex) => {
              const paramPath = `${procPath}.parameters[${paramIndex}]`

              cb(paramPath, param, {
                prodIdx: productIndex,
                lineIdx: lineIndex,
                stageIdx: stageIndex,
                procIdx: procIndex,
                paramIdx: paramIndex,
              })
            })
          })
        })
      })
    }
  )
}

export function setFieldsToSubmitted({
  form,
  schema,
}: {
  form: UseFormReturn
  schema: FormSchema
}) {
  const preserveStatuses: FormParamStatus[] = [
    FormParamStatus.SUBMITTED,
    FormParamStatus.NOT_APPLICABLE,
    FormParamStatus.BREAK_DOWN,
    FormParamStatus.CHANGE_OVER,
    FormParamStatus.POWER_FAILURE,
    FormParamStatus.OTHER,
  ]

  for (const [prodIdx, product] of schema.products.entries()) {
    const prodPath = `products[${prodIdx}]`

    for (const [lineIdx, line] of product.productionLines.entries()) {
      const linePath = `${prodPath}.productionLines[${lineIdx}]`

      for (const [stageIdx, stage] of line.machines.entries()) {
        const stagePath = `${linePath}.machines[${stageIdx}]`

        for (const [procIdx, proc] of stage.internalProcesses.entries()) {
          const procPath = `${stagePath}.internalProcesses[${procIdx}]`

          for (const [paramIdx] of proc.parameters.entries()) {
            const paramPath = `${procPath}.parameters[${paramIdx}]`

            const value = form.getValues(paramPath)

            if (value?.window?.length) {
              for (const [index] of value.window.entries()) {
                const path = `${paramPath}.window[${index}]`
                const winValue = form.getValues(path)

                if (
                  !winValue?.status ||
                  !preserveStatuses.includes(winValue?.status)
                ) {
                  form.setValue(`${path}.status`, FormParamStatus.SUBMITTED)
                }
              }
            } else {
              const paramValue = form.getValues(paramPath)

              if (
                !paramValue?.status ||
                !preserveStatuses.includes(paramValue?.status)
              ) {
                form.setValue(`${paramPath}.status`, FormParamStatus.SUBMITTED)
              }
            }
          }
        }
      }
    }
  }
}
